import { useState } from "react";
import { AlertCircle, CheckCircle2, Send } from "lucide-react";
import { sendMessage } from "../firebase/api";
import { trackLead } from "../lib/pixel";
import { SITE } from "../config/site";
import { useI18n } from "../i18n";

// Values are stored in Serbian (the office reads them); labels are translated.
export const INTEREST_OPTIONS = ["Jednosoban", "Dvosoban", "Trosoban i veći", "Lokal"];

const PHONE_RE = /^[+()\d\s/-]{6,20}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

function validate(values, { requireMessage }) {
  const errors = {};
  if (values.ime.trim().length < 2) errors.ime = "name";
  if (!PHONE_RE.test(values.telefon.trim())) errors.telefon = "phone";
  if (values.email.trim() && !EMAIL_RE.test(values.email.trim())) errors.email = "email";
  if (requireMessage && values.poruka.trim().length < 3) errors.poruka = "message";
  return errors;
}

const EMPTY = { ime: "", telefon: "", email: "", poruka: "", interesovanje: "", website: "" };

/**
 * Shared inquiry form.
 * - source: where the inquiry came from (pocetna | katalog | landing), shown in the admin
 * - unit: Serbian unit description saved with the message (e.g. "Lamela A · Stan 12 · ...")
 * - unitLabel: the same description in the visitor's language (display only)
 * - showInterest: shows the layout chips (landing page)
 * - onSuccess: when set, the parent takes over after sending (e.g. a redirect)
 */
export default function InquiryForm({
  source,
  unit,
  unitLabel,
  showInterest = false,
  requireMessage = false,
  submitLabel,
  messagePlaceholder,
  onSuccess,
  idPrefix = "upit",
}) {
  const { t, lang } = useI18n();
  const [values, setValues] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | sending | sent | error

  const set = (name) => (e) => {
    const value = e.target.value;
    setValues((v) => ({ ...v, [name]: value }));
    if (errors[name]) setErrors((err) => ({ ...err, [name]: undefined }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (values.website) return; // honeypot: bots fill in the hidden field
    const found = validate(values, { requireMessage });
    setErrors(found);
    if (Object.keys(found).length) {
      document.getElementById(`${idPrefix}-${Object.keys(found)[0]}`)?.focus();
      return;
    }
    setStatus("sending");
    try {
      await sendMessage({ ...values, izvor: source, stan: unit, jezik: lang });
      if (onSuccess) {
        onSuccess(values);
      } else {
        trackLead(source, unit ? { unit } : {});
        setStatus("sent");
        setValues(EMPTY);
      }
    } catch (error) {
      console.error("Sending the inquiry failed:", error);
      setStatus("error");
    }
  };

  if (status === "sent") {
    return (
      <div className="notice notice--success" role="status" data-testid="inquiry-success">
        <CheckCircle2 size={22} />
        <div>
          <strong>{t("form.sentTitle")}</strong>
          <p>{t("form.sentText")}</p>
        </div>
      </div>
    );
  }

  const errorText = (name) => errors[name] && t(`form.errors.${errors[name]}`);

  const field = (name, label, props = {}, optional = false) => (
    <div className="field">
      <label htmlFor={`${idPrefix}-${name}`}>
        {label} {optional && <span className="optional">({t("common.notRequired")})</span>}
      </label>
      <input
        id={`${idPrefix}-${name}`}
        name={name}
        className="input"
        value={values[name]}
        onChange={set(name)}
        aria-invalid={errors[name] ? "true" : undefined}
        aria-describedby={errors[name] ? `${idPrefix}-${name}-err` : undefined}
        {...props}
      />
      {errors[name] && (
        <span className="field-error" id={`${idPrefix}-${name}-err`}>
          {errorText(name)}
        </span>
      )}
    </div>
  );

  return (
    <form className="inquiry-form" onSubmit={handleSubmit} noValidate>
      {unit && (
        <p className="inquiry-unit">
          {t("form.unitFor")} <strong>{unitLabel || unit}</strong>
        </p>
      )}
      {showInterest && (
        <fieldset className="field inquiry-interest">
          <legend className="field-label">{t("form.interest")}</legend>
          <div className="chips">
            {INTEREST_OPTIONS.map((option) => (
              <button
                type="button"
                key={option}
                className="chip"
                aria-pressed={values.interesovanje === option}
                onClick={() =>
                  setValues((v) => ({ ...v, interesovanje: v.interesovanje === option ? "" : option }))
                }
              >
                {t(`form.interestOptions.${option}`)}
              </button>
            ))}
          </div>
        </fieldset>
      )}
      <div className="inquiry-grid">
        {field("ime", t("form.name"), { autoComplete: "name", required: true })}
        {field("telefon", t("form.phone"), {
          type: "tel",
          autoComplete: "tel",
          inputMode: "tel",
          placeholder: t("form.phonePlaceholder"),
          required: true,
        })}
      </div>
      {field("email", t("form.email"), { type: "email", autoComplete: "email" }, true)}
      <div className="field">
        <label htmlFor={`${idPrefix}-poruka`}>
          {t("form.message")} {!requireMessage && <span className="optional">({t("common.notRequired")})</span>}
        </label>
        <textarea
          id={`${idPrefix}-poruka`}
          name="poruka"
          className="textarea"
          rows={3}
          placeholder={messagePlaceholder || t("form.messagePlaceholder")}
          value={values.poruka}
          onChange={set("poruka")}
          aria-invalid={errors.poruka ? "true" : undefined}
        />
        {errors.poruka && <span className="field-error">{errorText("poruka")}</span>}
      </div>
      <div className="hp-field" aria-hidden="true">
        <label htmlFor={`${idPrefix}-website`}>{t("form.honeypot")}</label>
        <input
          id={`${idPrefix}-website`}
          name="website"
          tabIndex={-1}
          autoComplete="off"
          value={values.website}
          onChange={set("website")}
        />
      </div>

      {status === "error" && (
        <div className="notice notice--error" role="alert">
          <AlertCircle size={20} />
          <p>
            {t("form.failed")}{" "}
            <a href={SITE.phoneHref} className="link">
              {SITE.phoneDisplay}
            </a>
            .
          </p>
        </div>
      )}

      <button type="submit" className="btn btn--accent btn--lg btn--block" disabled={status === "sending"}>
        {status === "sending" ? <span className="spinner spinner--light" /> : <Send size={18} />}
        {status === "sending" ? t("form.sending") : submitLabel || t("form.submit")}
      </button>
      <p className="inquiry-note">{t("form.privacy")}</p>
    </form>
  );
}
